import { useState } from 'react';
import Switch from '@mui/material/Switch';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { OpenInBrowser } from '@mui/icons-material';
import { fraunces } from '../../Landing/fonts';
import { Eyebrow, INK, MUTED } from '../../Landing/ui';
import HostingV2Domain from './HostingV2Domain';
import OpenV2EditorButton from './OpenV2EditorButton';
import Plan from './Plan';
import planName from '../../../helpers/planName';

dayjs.extend(relativeTime);

function Hairline() {
  return (
    <div className="tw-my-6 tw-border-0 tw-border-t tw-border-solid tw-border-[#E7E0CF] dark:tw-border-[#26382F]" />
  );
}

// A settings row: title + one short line on the left, the control on the
// right. The v1 OnOffSwitch's justified text wall stays in v1.
function Row({ title, children, control }) {
  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-6">
      <div className="tw-min-w-0">
        <p className={`${INK} tw-m-0 tw-font-semibold`}>{title}</p>
        <p
          className={`${MUTED} tw-m-0 tw-mt-1 tw-max-w-xl tw-text-sm tw-leading-relaxed`}
        >
          {children}
        </p>
      </div>
      {control !== undefined && <div className="tw-shrink-0">{control}</div>}
    </div>
  );
}

// The dashboard surface for a blog on the new hosting. Design, pages,
// appearance, integrations, and analytics all live in the site editor — the
// dashboard keeps only what it owns: the editor entry, the cross-blog posting
// default, the custom domain (HostingV2Domain), and plan/billing. The v1 tabs and the v1 header
// strip never render for these blogs.
// What the plan row says, and whether it needs attention. A Starter blog's
// hosting is a 30-day trial (hostingTrialExpiryDate) inside a yearly plan;
// a free trial ends at expiryDate; a paid plan only ends once cancelled.
function planStatus(blog) {
  const name = planName(blog.plan);
  const ends = date => dayjs(date).fromNow();
  const past = date => new Date(date) < new Date();
  const soon = date => dayjs(date).subtract(3, 'day').toDate() < new Date();
  if (blog.plan === 'trial')
    return {
      line: `Free trial — ${past(blog.expiryDate) ? 'ended' : 'ends'} ${ends(blog.expiryDate)}`,
      warn: soon(blog.expiryDate),
      choose: true,
    };
  if (blog.plan === 'starter' && blog.hostingTrialExpiryDate)
    return {
      line: `Starter plan — your blog trial ${past(blog.hostingTrialExpiryDate) ? 'ended' : 'ends'} ${ends(blog.hostingTrialExpiryDate)}`,
      warn: soon(blog.hostingTrialExpiryDate),
      choose: true,
    };
  if (past(blog.expiryDate))
    return { line: `${name} plan — ended ${ends(blog.expiryDate)}`, warn: true, choose: true };
  if (blog.isCancelled)
    return { line: `${name} plan — ends ${ends(blog.expiryDate)}`, warn: true, choose: false };
  return { line: `You are on the ${name} plan.`, warn: false, choose: false };
}

export default function HostingV2Panel(props) {
  const { blog, blogId, handleEditV2, defaultAddPosts, setDefaultAddPosts } = props;
  // Back from Stripe checkout: open the plan section, where the payment
  // confirmation shows.
  const [showPlan, setShowPlan] = useState(!!props.paymentSuccess);
  const origin = blog.domain || `https://${blogId}.truvvl.blog`;
  const { line: planLine, warn: endsSoon, choose } = planStatus(blog);

  return (
    <div className="tw-pt-1">
      <Eyebrow className="tw-mb-2">TravelFeed Hosting 2.0</Eyebrow>
      <h3
        className={`${fraunces.className} ${INK} tw-m-0 tw-mb-2 tw-text-xl tw-font-semibold tw-tracking-tight`}
      >
        Your site lives in the editor
      </h3>
      <p className={`${MUTED} tw-mt-0 tw-mb-5 tw-max-w-2xl tw-leading-relaxed`}>
        Design, pages, appearance, and reader analytics are all managed in the
        site editor. Changes go live seconds after you publish.
      </p>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
        {blog.editorAvailable && <OpenV2EditorButton blogId={blogId} journal />}
        <a
          href={origin}
          target="_blank"
          rel="nofollow noreferrer noopener"
          className={`tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-bg-transparent tw-cursor-pointer tw-px-6 tw-py-2.5 tw-text-sm tw-font-semibold tw-no-underline tw-border tw-border-solid tw-border-[#C9C0AA] dark:tw-border-[#3A4D44] ${INK} hover:tw-border-primary-600 hover:tw-text-primary-700 dark:hover:tw-text-primary-300 tw-transition-colors`}
        >
          Open site <OpenInBrowser fontSize="small" />
        </a>
      </div>
      <Hairline />
      <Row
        title="Add new posts automatically"
        control={
          <Switch
            color="primary"
            checked={defaultAddPosts || false}
            onChange={event => {
              // One toggle, one setting — it saves itself; a Save button
              // for a single switch is furniture.
              const next = event.target.checked;
              setDefaultAddPosts(next);
              handleEditV2(next);
            }}
            inputProps={{ 'aria-label': 'Add new posts to this blog by default' }}
          />
        }
      >
        Posts you publish on TravelFeed join this blog by default. You can
        adjust every post under &quot;Visibility&quot; in the post editor.
      </Row>
      <Hairline />
      <HostingV2Domain
        blog={blog}
        blogId={blogId}
        refetchHostingPlan={props.refetchHostingPlan}
      />
      <Hairline />
      <Row
        title="Plan"
        control={
          <button
            type="button"
            onClick={() => setShowPlan(current => !current)}
            className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-solid tw-border-primary-600 tw-bg-transparent tw-cursor-pointer tw-px-5 tw-py-2 tw-text-sm tw-font-semibold tw-text-primary-700 dark:tw-text-primary-300 hover:tw-bg-primary-600 hover:tw-text-white tw-transition-colors"
          >
            {showPlan ? 'Hide' : choose ? 'Choose a plan' : 'Manage billing'}
          </button>
        }
      >
        <span
          className={
            endsSoon ? 'tw-text-red-600 dark:tw-text-red-400' : undefined
          }
        >
          {planLine}
        </span>
      </Row>
      {showPlan && (
        <div className="tw-mt-5">
          <Plan {...props} />
        </div>
      )}
    </div>
  );
}
